import { createBigDecimal, toStringBigDecimal } from '@0xtorch/big-decimal'
import type { Action } from '@0xtorch/core'
import { AssetNotFoundException } from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import { hyperliquidAccountCategory } from '../constants'
import { createClient } from '../createClient'
import { responseSchema } from './schema'

export const fetchApiData: FetchApiData = async ({
  apiKey,
  assets,
  config,
  from,
  createHeaders,
  proxy,
  symbolAssetMap,
  to,
}) => {
  const getAsset = createGetAsset({ assets, symbolAssetMap })

  const client = createClient({
    apiKey,
    createHeaders,
    proxy,
  })

  const response = await client.run({
    method: 'POST',
    path: '/info',
    body: {
      type: 'userNonFundingLedgerUpdates',
      startTime: from,
      ...(to ? { endTime: to } : {}),
    },
    responseSchema,
  })

  // 昇順 sort by time
  const sortedFundings = response.sort((a, b) => a.time - b.time)

  // データ処理
  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = []
  const actions: Action[] = []

  for (const item of sortedFundings) {
    // Update fetchedFrom and fetchedTo
    fetchedFrom =
      fetchedFrom === undefined ? item.time : Math.min(fetchedFrom, item.time)
    fetchedTo =
      fetchedTo === undefined ? item.time : Math.max(fetchedTo, item.time)

    let symbol = 'USDC'
    if (item.delta.token) {
      symbol = item.delta.token
    }
    if (item.delta.feeToken) {
      symbol = item.delta.feeToken
    }
    const asset = getAsset(symbol)
    if (!asset) {
      throw new AssetNotFoundException(symbol)
    }

    switch (item.delta.type) {
      // airdrop
      case 'spotGenesis': {
        if (
          !item.delta.token ||
          !item.delta.amount ||
          item.delta.amount.value < 0n
        ) {
          throw new Error(
            `Invalid spotGenesis delta: ${JSON.stringify(item.delta)}`,
          )
        }
        if (item.delta.amount.value === 0n) {
          break
        }
        actions.push({
          comment: item.delta.type,
          evidence: 'system-rule',
          order: 0,
          source: `${hyperliquidAccountCategory}_${config.type}_${item.hash}`,
          type: 'airdrop',
          timestamp: item.time,
          transfers: [
            {
              asset,
              amount: item.delta.amount,
              direction: 'in',
            },
          ],
        })
        break
      }
      // gift
      case 'rewardsClaim':
      case 'vaultDistribution':
      case 'vaultLeaderCommission': {
        const rewardAmount = item.delta.amount ?? item.delta.usdc
        if (!rewardAmount || rewardAmount.value < 0n) {
          throw new Error(`Invalid delta: ${JSON.stringify(item.delta)}`)
        }
        if (rewardAmount.value === 0n) {
          break
        }
        actions.push({
          comment: item.delta.type,
          evidence: 'system-rule',
          order: 0,
          source: `${hyperliquidAccountCategory}_${config.type}_${item.hash}`,
          type: 'gift',
          timestamp: item.time,
          transfers: [
            {
              asset,
              amount: rewardAmount,
              direction: 'in',
            },
          ],
        })
        break
      }
      // vault
      case 'vaultWithdraw': {
        if (item.delta.commission && item.delta.commission.value > 0n) {
          actions.push({
            comment: `Commision of ${item.delta.type}${item.delta.vault ? ` | Vault: ${item.delta.vault}` : ''}`,
            evidence: 'system-rule',
            order: 0,
            source: `${hyperliquidAccountCategory}_${config.type}_${item.hash}`,
            type: 'gift',
            timestamp: item.time,
            transfers: [
              {
                asset,
                amount: item.delta.commission,
                direction: 'in',
              },
            ],
          })
        }
        if (item.delta.closingCost && item.delta.closingCost.value > 0n) {
          actions.push({
            comment: `Closing Cost of ${item.delta.type}${item.delta.vault ? ` | Vault: ${item.delta.vault}` : ''}`,
            evidence: 'system-rule',
            order: 0,
            source: `${hyperliquidAccountCategory}_${config.type}_${item.hash}`,
            type: 'fee',
            timestamp: item.time,
            transfers: [
              {
                asset,
                amount: item.delta.closingCost,
                direction: 'out',
              },
            ],
          })
        }
        break
      }
      // fee
      case 'accountClassTransfer':
      case 'cStakingTransfer':
      case 'deposit':
      case 'internalTransfer':
      case 'spotTransfer':
      case 'vaultCreate':
      case 'vaultDeposit':
      case 'withdraw': {
        if (item.delta.fee === undefined || item.delta.fee.value === 0n) {
          break
        }
        if (item.delta.fee.value < 0n) {
          throw new Error(`Negative fee not allowed: ${item.delta.fee.value}`)
        }
        actions.push({
          comment: `Fee of ${item.delta.type} | ${toStringBigDecimal(item.delta.amount ?? item.delta.usdc ?? createBigDecimal(0n))} ${item.delta.token ?? 'USDC'}${item.delta.user && item.delta.destination ? ` (${item.delta.user} -> ${item.delta.destination})` : ''}`,
          evidence: 'system-rule',
          order: 0,
          source: `${hyperliquidAccountCategory}_${config.type}_${item.hash}`,
          type: 'fee',
          timestamp: item.time,
          transfers: [
            {
              asset,
              amount: item.delta.fee,
              direction: 'out',
            },
          ],
        })
        break
      }
      default: {
        throw new Error(`Unsupported delta type: ${item.delta.type}`)
      }
    }
  }

  // Sort actions by timestamp in ascending order
  const sortedActions = actions.sort((a, b) => a.timestamp - b.timestamp)

  return {
    actions: sortedActions,
    count: response.length,
    from: fetchedFrom,
    sources,
    stopped: false,
    to: fetchedTo,
  }
}
